import { apiRequest } from './core/client';

// ============================================================================
// Briefing Types
// ============================================================================

export type BriefingItemStatus = 'active' | 'dismissed' | 'actioned';

export interface BriefingMessageRef {
  message_id: string;
  account_id: string | null;
}

/**
 * A briefing item is a typed block written by the /briefing agent. `kind` is
 * open-ended by design: known kinds get rich rendering, unknown kinds fall
 * back to a generic card — never assume the list below is exhaustive.
 */
export interface BriefingItem {
  id: string;
  accountId: string;
  key: string;
  kind: string;
  priority: number;
  title: string;
  bodyMd: string | null;
  data: Record<string, unknown>;
  messageRefs: BriefingMessageRef[];
  status: BriefingItemStatus;
  expiresAt: string | null;
  dismissedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Whether the most recent briefing run restated this item. Always true under
   * the default scope (the UI never asks for more), so the panel is unaffected;
   * it exists for the agent's `scope=all` read, where `active` but not `live`
   * means "dropped from the panel without anyone handling it".
   */
  live?: boolean | null;
}

export interface Briefing {
  id: string;
  accountId: string;
  generatedAt: string;
  model: string | null;
  runSource: 'scheduled' | 'manual';
  notes: string | null;
  stats: Record<string, unknown>;
}

export interface BriefingResponse {
  briefing: Briefing | null;
  items: BriefingItem[];
}

// ============================================================================
// Briefing Operations
// ============================================================================

export async function fetchBriefing(
  accountId: string,
  options: { includeDismissed?: boolean; signal?: AbortSignal } = {}
): Promise<BriefingResponse> {
  const response = await apiRequest<unknown>({
    path: `/accounts/${encodeURIComponent(accountId)}/briefing`,
    method: 'GET',
    query: options.includeDismissed ? { includeDismissed: true } : undefined,
    signal: options.signal,
  });

  const record = response as Record<string, unknown>;
  return {
    briefing: (record.briefing as Briefing | null) ?? null,
    items: Array.isArray(record.items) ? (record.items as BriefingItem[]) : [],
  };
}

export async function setBriefingItemStatus(
  accountId: string,
  key: string,
  status: BriefingItemStatus
): Promise<{ success: boolean; item: BriefingItem | null }> {
  const response = await apiRequest<unknown>({
    path: `/accounts/${encodeURIComponent(accountId)}/briefing/items/${encodeURIComponent(key)}/status`,
    method: 'POST',
    body: { status },
  });
  const record = response as Record<string, unknown>;
  return {
    success: Boolean(record.success),
    item: (record.item as BriefingItem | null) ?? null,
  };
}
